import React from 'react'
import { compact } from 'lodash'

import PlainText from '../UI/PlainText'
import { LogoHeader, ProjectSummary, QuoteFooter } from './PrintableQuoteHeaders'

import './styles/actuals.css'

/*
MAIN COMPONENT -----------------------------------------------------------------

Props:
- actual: an actual object
- project: a project attribute INCLUDING:
  agency, client, user and agencyProducer
- print: Boolean: adapt actual to printing (default: false)
*/
const ClientFacingActual = props => {
  const { actual, project, print } = props
  const agency = project.included('agency')
  const client = project.included('client')
  const user = project.included('user')
  const agencyProducer = project.included('agencyProducer')
  const projectTypeAddress = project.attributes.projectTypeAddress
//  const docketNumber = project.id;
  const docketNumber = project.attributes.formattedDocketNo;
  const summary = actual.attributes.formattedSummary
  const categories = summary.categories

  // Sorting categories properly for display
  let orderedCategoryKeys = []
  for (let categoryName in categories) {
    let index = Number(categories[categoryName].order)
    // Avoid overwriting items - we may have services with the same order.
    // In this case, keep adding 1 until we're not overwriting anything.
    // NOTE: Possibly an ugly hack that may bite me one day?
    while (orderedCategoryKeys[index]) {
      index += 1
    }
    orderedCategoryKeys[index] = categoryName
  }
  // Close gaps in array to make sure nothing blows up
  orderedCategoryKeys = compact(orderedCategoryKeys)

  const cssClassList = ['printable-actual']
  if (!print) cssClassList.push('client-facing-actual-screen')
  const cssClasses = cssClassList.join(' ')

  return (
    <section className={cssClasses}>
      <LogoHeader
        title="ACTUALS"
        subtitle="THIS IS NOT AN INVOICE"
        docketNumber={docketNumber}
      />

      <ProjectSummary
        project={project}
        actual={actual}
        agency={agency}
        client={client}
        agencyProducer={agencyProducer}
        user={user}
      />

      <table className="actual-table">
        <thead>
          <tr>
            <th className="text-header">CATEGORY</th>
            <th className="text-header">
              SERVICE
            </th>
            <th className="span-header thick-left thick-top">
              ESTIMATE
            </th>
            <th
              colSpan={3}
              className="span-header thick-left thick-top thick-right"
            >
              ACTUALS
            </th>
          </tr>
          <tr>
            <th colSpan={2} className="span-header" />
            <th className="number-header thick-left">Total</th>

            <th className="number-header thick-left">Qty</th>
            <th className="number-header">Rate</th>
            <th className="number-header thick-right">Total</th>
          </tr>
        </thead>

        {orderedCategoryKeys.map(category => (
          <tbody key={`tbody-${category}`}>
            {categories[category].lineItems
              .sort((a, b) => a.order - b.order)
              .map((lineItem, index) => (
                <tr
                  key={`tr-${category}-item-${index}`}
                  className={index === 0 ? 'top-row' : ''}
                >
                  <td className="category-column">
                    {index === 0 ? category : ''}
                  </td>

                  <td className="service-column">
                    <PlainText
                      className="service-description"
                      text={lineItem.description}
                    />
                  </td>

                  <td className="number-column thick-left dark-shaded">
                    {lineItem.formattedTotal}
                  </td>

                  <td className="number-column thick-left shaded">
                    {lineItem.actualNumber}
                  </td>

                  <td className="number-column shaded">
                    {lineItem.formattedActualRateIncludingAdjustment}
                  </td>

                  <td className="number-column thick-right dark-shaded">
                    {lineItem.formattedActualTotal}
                  </td>

                </tr>
              ))}

            <tr key={`tr-${category}-total`}>
              <th colSpan={2} className="total-header">
                {category} totals
              </th>

              <td className="category-total-price thick-left dark-shaded">
                {categories[category].formattedSubTotal}
              </td>

              <td className="category-total-price thick-left shaded" />

              <td className="category-total-price shaded" />

              <td className="category-total-price thick-right dark-shaded">
                {categories[category].formattedActualSubTotal}
              </td>
            </tr>

            <tr key={`tr-${category}-total-sep`} className="tr-separator">
              <td colSpan={6} />
            </tr>
          </tbody>
        ))}

        <tbody>
          {actual.attributes.notes && (
            <tr>
              <td colSpan={6} className="shaded">
                Notes:
                <PlainText text={actual.attributes.notes} />
              </td>
            </tr>
          )}
          <tr className="tr-separator">
            <td colSpan={6} />
          </tr>

          <tr>
              { (actual.attributes.totalDiscounts !== 0.00 && (
                <React.Fragment>
                  <td className="category-total-title">Total Discounts Applied:</td>
                  <td className="special-total-discount-amount">
                    <div className="special-total-discount-amount-inner">{actual.attributes.formattedTotalDiscounts}</div>
                    <div style={{float: 'right'}} className="total-header">Total (before taxes)</div>
                  </td>
                  </React.Fragment>
              )) || (
                <React.Fragment>
                  <td/>
                  <td className="total-header">
                    Total (before taxes)
                  </td>
                </React.Fragment>
              )}
            <td className="category-total-price thick-left dark-shaded">
              {actual.attributes.formattedGrossTotal}
            </td>
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price thick-right dark-shaded">
              {actual.attributes.formattedActualGrossTotal}
            </td>
          </tr>

          <tr>
            <th />
            <th className="total-header">
              Taxes ({actual.attributes.taxCode})
            </th>
            <td className="category-total-price thick-left dark-shaded" />
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price thick-right dark-shaded">
              {actual.attributes.formattedActualTaxes}
            </td>
          </tr>

          <tr>
            <th />
            <th className="total-header">
              Grand total
            </th>
            <td className="category-total-price thick-left dark-shaded" />
            <td className="category-total-price thick-left shaded" />
            <td className="category-total-price shaded" />
            <td className="category-total-price thick-right dark-shaded">
              {actual.attributes.formattedActualGrandTotal}
            </td>
          </tr>
        </tbody>
      </table>
      <QuoteFooter projectTypeAddress={projectTypeAddress}/>
    </section>
  )
}

export default ClientFacingActual



// WEBPACK FOOTER //
// ./src/components/Quotes/ClientFacingActual.jsx